import { useState } from "react";
import {
  UserPlus,
  Trash2,
  Send,
  ShieldCheck,
  ArrowLeft,
  CheckCircle2,
  Scale,
} from "lucide-react";

const content = {
  en: {
    title: "Guest Registration",
    subtitle: "Required by law for all foreign guests",
    intro:
      "Please provide the details of every guest staying at Casa da Porta Amarela. This information is required so we can report your stay to the competent authorities through SIBA (the accommodation bulletin system), as required by Portuguese law.",
    stayTitle: "Stay dates",
    checkIn: "Check-in (arrival)",
    checkOut: "Check-out (departure)",
    guest: "Guest",
    surname: "Surname(s)",
    surnamePh: "e.g. Silva",
    givenName: "Given name(s)",
    givenNamePh: "e.g. José",
    birthDate: "Date of birth",
    nationality: "Nationality",
    nationalityPh: "e.g. Brazilian",
    residence: "Country of residence",
    residencePh: "e.g. Brazil",
    docType: "Identification document",
    passport: "Passport",
    nationalId: "National identification document",
    docNumber: "Document number",
    docNumberPh: "Document number",
    docCountry: "Document issuing country",
    docCountryPh: "e.g. Brazil",
    foreignNote:
      "SIBA reporting applies to foreign citizens, including EU nationals. Portuguese nationals are exempt, but you may still fill it in.",
    addPerson: "Add person",
    removePerson: "Remove",
    submit: "Submit registration",
    sending: "Sending...",
    success: "Registration sent successfully. Thank you!",
    errorSend:
      "Something went wrong while sending. Please try again, or contact us directly.",
    backToSite: "Back to site",
    gdprTitle: "Data protection (GDPR)",
    gdprText:
      "The personal data submitted through this form is collected solely to comply with the legal obligation to report guest accommodation to the competent authorities through SIBA, as required by Law no. 23/2007. The lawful basis for processing is compliance with a legal obligation (Article 6(1)(c) GDPR). The data is not used for marketing nor shared with third parties beyond that legal obligation, and is kept only for as long as necessary to fulfil it.",
    legalTitle: "Legal basis for the registration requirement",
    legalItems: [
      "Articles 15 and 16 of Law no. 23/2007 of 4 July (the Portuguese Foreigners Act) — accommodation providers must report the lodging of foreign citizens via an accommodation bulletin.",
      "Article 45 of the Convention Implementing the Schengen Agreement.",
      "Ordinance no. 287/2007 (creates SIBA) and Ordinance no. 415/2008 (approves the bulletin model and electronic reporting), as amended by Ordinance no. 321/2023.",
      "The report must be submitted within three business days of check-in (and of check-out, if the departure date was not provided initially).",
      "The obligation applies to all foreign citizens, including EU nationals; Portuguese nationals are exempt.",
    ],
    note: "When you submit, your details are sent securely to us. You don't need an email app.",
    requiredMsg:
      "Please fill in at least the given name of the first guest before submitting.",
  },
  pt: {
    title: "Registo de Hóspedes",
    subtitle: "Exigido por lei para todos os hóspedes estrangeiros",
    intro:
      "Por favor, indique os dados de todos os hóspedes que ficarão na Casa da Porta Amarela. Esta informação é necessária para comunicarmos a sua estadia às autoridades competentes através do SIBA (Sistema de Informação de Boletins de Alojamento), conforme exigido pela lei portuguesa.",
    stayTitle: "Datas da estadia",
    checkIn: "Check-in (entrada)",
    checkOut: "Check-out (saída)",
    guest: "Hóspede",
    surname: "Apelido(s)",
    surnamePh: "ex: Silva",
    givenName: "Nome(s) próprio(s)",
    givenNamePh: "ex: José",
    birthDate: "Data de nascimento",
    nationality: "Nacionalidade",
    nationalityPh: "ex. Brasileira",
    residence: "País de residência",
    residencePh: "ex. Brasil",
    docType: "Documento de identificação",
    passport: "Passaporte",
    nationalId: "Documento de identificação nacional",
    docNumber: "Número do documento",
    docNumberPh: "Número do documento",
    docCountry: "País emissor do documento",
    docCountryPh: "ex. Brasil",
    foreignNote:
      "A comunicação ao SIBA aplica-se a cidadãos estrangeiros, incluindo da União Europeia. Os cidadãos portugueses estão isentos, mas pode preencher na mesma.",
    addPerson: "Adicionar pessoa",
    removePerson: "Remover",
    submit: "Submeter registo",
    sending: "A enviar...",
    success: "Registo enviado com sucesso. Obrigado!",
    errorSend:
      "Ocorreu um erro ao enviar. Por favor, tente novamente ou contacte-nos diretamente.",
    backToSite: "Voltar ao site",
    gdprTitle: "Proteção de dados (RGPD)",
    gdprText:
      "Os dados pessoais introduzidos neste formulário são recolhidos exclusivamente para cumprir a obrigação legal de comunicação do alojamento de hóspedes às autoridades competentes através do SIBA, conforme exigido pela Lei n.º 23/2007. O fundamento de licitude do tratamento é o cumprimento de uma obrigação jurídica (artigo 6.º, n.º 1, alínea c) do RGPD). Os dados não são utilizados para marketing nem partilhados com terceiros para além dessa obrigação legal, e são conservados apenas pelo período necessário ao seu cumprimento.",
    legalTitle: "Enquadramento legal da obrigação de registo",
    legalItems: [
      "Artigos 15.º e 16.º da Lei n.º 23/2007, de 4 de julho (Regime de entrada, permanência, saída e afastamento de estrangeiros) — quem proporciona alojamento deve comunicar o alojamento de cidadãos estrangeiros através do boletim de alojamento.",
      "Artigo 45.º da Convenção de Aplicação do Acordo de Schengen.",
      "Portaria n.º 287/2007 (cria o SIBA) e Portaria n.º 415/2008 (aprova o modelo do boletim e a comunicação eletrónica), com as alterações da Portaria n.º 321/2023.",
      "A comunicação deve ser efetuada no prazo de três dias úteis após o check-in (e após o check-out, caso a data de saída não tenha sido indicada inicialmente).",
      "A obrigação aplica-se a todos os cidadãos estrangeiros, incluindo da União Europeia; os cidadãos portugueses estão isentos.",
    ],
    note: "Ao submeter, os seus dados são enviados de forma segura para nós. Não precisa de aplicação de email.",
    requiredMsg:
      "Por favor, preencha pelo menos o nome próprio do primeiro hóspede antes de submeter.",
  },
};

const emptyGuest = () => ({
  surname: "",
  givenName: "",
  birthDate: "",
  nationality: "",
  residence: "",
  docType: "passport",
  docNumber: "",
  docCountry: "",
});

const firstGivenName = (g) =>
  (g.givenName || g.surname || "").trim().split(/\s+/)[0] || "";

export default function Registry() {
  const [lang, setLang] = useState("pt");
  const [stay, setStay] = useState({ checkIn: "", checkOut: "" });
  const [guests, setGuests] = useState([emptyGuest()]);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const t = content[lang];

  const updateGuest = (index, field, value) => {
    setGuests((prev) =>
      prev.map((g, i) => (i === index ? { ...g, [field]: value } : g))
    );
  };

  const addGuest = () => setGuests((prev) => [...prev, emptyGuest()]);

  const removeGuest = (index) =>
    setGuests((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async () => {
    const firstAvailable = guests.map(firstGivenName).find(Boolean);
    if (!firstAvailable) {
      setError(t.requiredMsg);
      return;
    }
    setError("");
    setStatus("sending");

    try {
      const res = await fetch("/.netlify/functions/register-guest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lang, stay, guests }),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("success");
      setStay({ checkIn: "", checkOut: "" });
      setGuests([emptyGuest()]);
    } catch {
      setStatus("error");
    }
  };

  const inputCls =
    "w-full rounded-lg border border-stone-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 bg-amber-50/40";
  const labelCls = "block text-sm font-medium text-stone-600 mb-1";

  return (
    <div className="min-h-screen bg-amber-50 text-stone-800 font-serif">
      {/* Header */}
      <header className="bg-white border-b-2 border-yellow-300 sticky top-0 z-50 shadow-sm">
        <div className="max-w-3xl mx-auto flex justify-between items-center px-6 py-3">
          <a href="/" className="flex items-center space-x-4 group">
            <img
              src="/logo.png"
              alt="Casa da Porta Amarela"
              className="h-16 w-auto object-contain"
            />
            <div>
              <h1 className="text-xl font-bold tracking-tight leading-tight text-stone-800 group-hover:text-yellow-600 transition-colors">
                Casa da Porta Amarela
              </h1>
              <p className="text-xs text-stone-500 tracking-widest uppercase">
                Ponta Delgada · Açores
              </p>
            </div>
          </a>
          <div className="flex space-x-2">
            <button
              onClick={() => setLang("en")}
              className={`rounded overflow-hidden transition-opacity ${
                lang === "en"
                  ? "opacity-100 ring-2 ring-yellow-400"
                  : "opacity-50 hover:opacity-80"
              }`}
            >
              <img
                src="https://flagcdn.com/w40/gb.png"
                alt="English"
                className="w-7 h-5 block"
              />
            </button>
            <button
              onClick={() => setLang("pt")}
              className={`rounded overflow-hidden transition-opacity ${
                lang === "pt"
                  ? "opacity-100 ring-2 ring-yellow-400"
                  : "opacity-50 hover:opacity-80"
              }`}
            >
              <img
                src="https://flagcdn.com/w40/pt.png"
                alt="Português"
                className="w-7 h-5 block"
              />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        {/* Hero */}
        <section className="text-center space-y-4">
          <div className="flex justify-center mb-2">
            <span className="inline-block w-12 h-1 rounded bg-yellow-400" />
          </div>
          <h2 className="text-4xl font-bold text-stone-800 tracking-tight">
            {t.title}
          </h2>
          <p className="text-lg italic text-yellow-700">{t.subtitle}</p>
          <p className="text-base text-stone-500 max-w-2xl mx-auto leading-relaxed">
            {t.intro}
          </p>
        </section>

        {/* Stay dates */}
        <section className="bg-white rounded-2xl shadow-sm border border-yellow-100 overflow-hidden">
          <div className="bg-yellow-400 px-6 py-3">
            <p className="font-bold text-stone-800">{t.stayTitle}</p>
          </div>
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{t.checkIn}</label>
              <input
                type="date"
                value={stay.checkIn}
                onChange={(e) =>
                  setStay((s) => ({ ...s, checkIn: e.target.value }))
                }
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>{t.checkOut}</label>
              <input
                type="date"
                value={stay.checkOut}
                onChange={(e) =>
                  setStay((s) => ({ ...s, checkOut: e.target.value }))
                }
                className={inputCls}
              />
            </div>
          </div>
        </section>

        {/* Guest forms */}
        <section className="space-y-6">
          {guests.map((g, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl shadow-sm border border-yellow-100 overflow-hidden"
            >
              <div className="bg-yellow-400 px-6 py-3 flex items-center justify-between">
                <p className="font-bold text-stone-800">
                  {t.guest} {i + 1}
                </p>
                {guests.length > 1 && (
                  <button
                    onClick={() => removeGuest(i)}
                    className="inline-flex items-center gap-1 text-stone-700 hover:text-red-700 text-sm transition-colors"
                  >
                    <Trash2 size={16} />
                    {t.removePerson}
                  </button>
                )}
              </div>
              <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t.givenName}</label>
                  <input
                    type="text"
                    value={g.givenName}
                    onChange={(e) =>
                      updateGuest(i, "givenName", e.target.value)
                    }
                    placeholder={t.givenNamePh}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t.surname}</label>
                  <input
                    type="text"
                    value={g.surname}
                    onChange={(e) => updateGuest(i, "surname", e.target.value)}
                    placeholder={t.surnamePh}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t.birthDate}</label>
                  <input
                    type="date"
                    value={g.birthDate}
                    onChange={(e) =>
                      updateGuest(i, "birthDate", e.target.value)
                    }
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t.nationality}</label>
                  <input
                    type="text"
                    value={g.nationality}
                    onChange={(e) =>
                      updateGuest(i, "nationality", e.target.value)
                    }
                    placeholder={t.nationalityPh}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t.residence}</label>
                  <input
                    type="text"
                    value={g.residence}
                    onChange={(e) =>
                      updateGuest(i, "residence", e.target.value)
                    }
                    placeholder={t.residencePh}
                    className={inputCls}
                  />
                </div>
                <div className="sm:col-start-1">
                  <label className={labelCls}>{t.docType}</label>
                  <select
                    value={g.docType}
                    onChange={(e) => updateGuest(i, "docType", e.target.value)}
                    className={inputCls}
                  >
                    <option value="passport">{t.passport}</option>
                    <option value="nationalId">{t.nationalId}</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{t.docNumber}</label>
                  <input
                    type="text"
                    value={g.docNumber}
                    onChange={(e) =>
                      updateGuest(i, "docNumber", e.target.value)
                    }
                    placeholder={t.docNumberPh}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t.docCountry}</label>
                  <input
                    type="text"
                    value={g.docCountry}
                    onChange={(e) =>
                      updateGuest(i, "docCountry", e.target.value)
                    }
                    placeholder={t.docCountryPh}
                    className={inputCls}
                  />
                </div>
              </div>
            </div>
          ))}

          <p className="text-xs text-stone-400 leading-relaxed">
            {t.foreignNote}
          </p>

          <button
            onClick={addGuest}
            className="inline-flex items-center gap-2 bg-white border-2 border-yellow-300 hover:border-yellow-400 hover:bg-yellow-50 text-stone-700 font-bold px-5 py-2.5 rounded-full shadow-sm transition-colors"
          >
            <UserPlus size={18} className="text-yellow-500" />
            {t.addPerson}
          </button>
        </section>

        {/* GDPR notice */}
        <section className="bg-white rounded-2xl shadow-sm border border-yellow-100 p-6">
          <h3 className="text-lg font-semibold text-stone-700 flex items-center gap-2 mb-2">
            <ShieldCheck className="text-yellow-500 shrink-0" size={20} />
            {t.gdprTitle}
          </h3>
          <p className="text-sm text-stone-500 leading-relaxed">{t.gdprText}</p>
        </section>

        {/* Legal basis */}
        <section className="bg-white rounded-2xl shadow-sm border border-yellow-100 p-6">
          <h3 className="text-lg font-semibold text-stone-700 flex items-center gap-2 mb-3">
            <Scale className="text-yellow-500 shrink-0" size={20} />
            {t.legalTitle}
          </h3>
          <ul className="space-y-2">
            {t.legalItems.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-stone-500 leading-relaxed"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 shrink-0 mt-2" />
                {item}
              </li>
            ))}
          </ul>
        </section>

        {/* Submit */}
        <section className="flex flex-col items-center gap-3">
          {error && <p className="text-sm text-red-600 font-medium">{error}</p>}

          {status === "success" ? (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 font-medium px-6 py-3 rounded-full">
              <CheckCircle2 size={20} />
              {t.success}
            </div>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={status === "sending"}
              className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 disabled:opacity-60 disabled:cursor-not-allowed text-stone-900 font-bold px-8 py-3 rounded-full shadow transition-colors"
            >
              <Send size={18} />
              {status === "sending" ? t.sending : t.submit}
            </button>
          )}

          {status === "error" && (
            <p className="text-sm text-red-600 font-medium text-center max-w-md">
              {t.errorSend}
            </p>
          )}

          <p className="text-xs text-stone-400 text-center max-w-md">{t.note}</p>
          <a
            href="/"
            className="inline-flex items-center gap-1 text-stone-500 hover:text-yellow-600 text-sm transition-colors mt-2"
          >
            <ArrowLeft size={16} />
            {t.backToSite}
          </a>
        </section>
      </main>

      <footer className="bg-white border-t-2 border-yellow-300 py-6 text-center text-sm text-stone-400 mt-8">
        © {new Date().getFullYear()} Casa da Porta Amarela · Ponta Delgada,
        Açores
      </footer>
    </div>
  );
}
